import React from 'react';
import { Icon, IconButton, Tooltip } from '@material-ui/core';
import CreateUpdateTaskModal from '../CreateUpdateTaskModal';
import { TaskLibraryListType } from '../../../../models/common';
import { useTranslation } from 'react-i18next';
import { AnimalType } from '../../../../models/animal';
import useAuthContext from '../../../../hooks/useAuthContext';
import UserRole from '../../../../constants/user-role';
import { HealthSystemDomainTaskLibraryResultTaskTemplateViewModel } from '../../../../api/HealthSystemApi';

interface TaskItemLibraryProps {
  editDisabled: boolean;
  task: HealthSystemDomainTaskLibraryResultTaskTemplateViewModel;
  onUpdateAction?: (result: boolean) => void;
  expectedEndTime?: string | null;
  // One-line summary for the library sidebar; lane cards keep the full details.
  compact?: boolean;
}

const TaskItemLibrary: React.FC<TaskItemLibraryProps> = ({
  editDisabled,
  task,
  onUpdateAction,
  expectedEndTime,
  compact
}) => {
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const [taskModalOpened, setTaskModalOpened] = React.useState(false);
  const vaccination = task.type === TaskLibraryListType.VaccinationRule ? task.vaccinationRule : null;
  const canEdit = !editDisabled && state.role !== UserRole.user;

  const modal = (
    <CreateUpdateTaskModal
      key={task.id}
      opened={taskModalOpened}
      task={task}
      onModalAction={(result) => {
        setTaskModalOpened(false);
        onUpdateAction?.(result);
      }}
    />
  );

  if (compact) {
    const summary = vaccination
      ? [
          AnimalType[vaccination.animalType],
          vaccination.medicalSupply?.number,
          `${vaccination.ageFrom}–${vaccination.ageTo} ${t('f-days')}`,
          `${vaccination.dose} CC`
        ]
      : [task.duration != null ? `${task.duration} ${task.durationUnit}` : null, task.scheduled];
    const summaryText = summary.filter(Boolean).join(' · ');

    return (
      <>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            className={
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-md ' +
              (vaccination ? 'bg-teal-50 text-teal-700' : 'bg-indigo-50 text-indigo-600')
            }
          >
            <Icon className="!text-sm/none">{vaccination ? 'vaccines' : 'checklist'}</Icon>
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium leading-5 text-slate-800" title={task.name ?? undefined}>
              {vaccination ? t('f-com-injection') : task.name}
            </div>
            {summaryText && (
              <div className="truncate text-xs leading-4 text-slate-500" title={summaryText}>
                {summaryText}
              </div>
            )}
          </div>
          {canEdit && (
            <Tooltip title={t('f-com-edit')}>
              <IconButton size="small" aria-label={t('f-com-edit')} onClick={() => setTaskModalOpened(true)}>
                <Icon className="!text-base/none">edit</Icon>
              </IconButton>
            </Tooltip>
          )}
        </div>
        {modal}
      </>
    );
  }

  return (
    <>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium leading-5 text-slate-800 break-words">
            {vaccination ? t('f-com-injection') : task.name}
          </span>
          {canEdit && (
            <Tooltip title={t('f-com-edit')}>
              <IconButton size="small" aria-label={t('f-com-edit')} onClick={() => setTaskModalOpened(true)}>
                <Icon className="!text-base/none">edit</Icon>
              </IconButton>
            </Tooltip>
          )}
        </div>
        {vaccination ? (
          <dl className="mt-2 mb-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-slate-500">
            <dt>{t('f-com-animal-type')}</dt>
            <dd className="m-0 text-slate-700">{AnimalType[vaccination.animalType]}</dd>
            <dt>{t('f-com-supply')}</dt>
            <dd className="m-0 text-slate-700 break-words">{vaccination.medicalSupply?.number || '—'}</dd>
            <dt>{t('f-days')}</dt>
            <dd className="m-0 text-slate-700">
              {vaccination.ageFrom}–{vaccination.ageTo}
            </dd>
            <dt>{t('f-com-dose')}</dt>
            <dd className="m-0 text-slate-700">{vaccination.dose} CC</dd>
          </dl>
        ) : (
          <div className="mt-1 text-xs leading-5 text-slate-500">
            {task.duration != null && (
              <span>
                {task.duration} {task.durationUnit}
              </span>
            )}
            {task.scheduled && <div>{task.scheduled}</div>}
          </div>
        )}
        {expectedEndTime && (
          <div className="mt-2 flex items-center gap-1 text-xs font-medium text-indigo-700">
            <Icon className="!text-sm/none">schedule</Icon>
            {t('f-tasks-expected-end-time')}: {expectedEndTime}
          </div>
        )}
      </div>
      {modal}
    </>
  );
};

export default TaskItemLibrary;
